/**
 * Utilitários para o benchmark personalizado do cliente.
 *
 * Centraliza a lógica de injeção (gráficos) e de parsing de fórmulas
 * que anteriormente estava duplicada em minha-carteira.vue,
 * carteira-analise.vue, Format.js e client-fields.vue.
 */

export const CLIENT_BENCHMARK_KEY = 'cliente_benchmark'
export const BENCHMARK_PERSONALIZADO_COLOR = '#C9A961'

// Labels do cliente_benchmark_meta que sao equivalentes ao CDI default do sistema.
// Quando o backend retorna meta com uma destas labels, o benchmark personalizado
// e matematicamente igual ao CDI default e deve ser tratado como ausente
// (evita duplicar a linha do CDI no chart e nos indicadores).
export const CDI_DEFAULT_META_LABELS = ['CDI', '100% CDI']

// Lista canônica de indexadores disponíveis para benchmark personalizado.
// Importada por client-fields.vue para popular o v-select de indexadores.
export const BENCHMARK_INDEXADORES = [
  { value: 'CDI', text: 'CDI' },
  { value: 'IPC-A', text: 'IPC-A' },
  { value: 'IBOV', text: 'IBOV' },
  { value: 'IFIX', text: 'IFIX' },
  { value: 'IMA-B', text: 'IMA-B' },
  { value: 'IRF-M', text: 'IRF-M' },
  { value: 'DÓLAR', text: 'Dólar' },
  { value: 'S&P 500', text: 'S&P 500' },
  { value: 'CPI', text: 'CPI' },
  { value: 'PREFIXADO', text: 'Prefixado' },
]

// String de alternativas para regex de parsing de fórmulas.
// Inclui aliases sem acento (DOLAR) e nomes completos (IBOVESPA).
const _INDEXADORES_REGEX_STR = [
  ...BENCHMARK_INDEXADORES.map((b) => b.value),
  'DOLAR',
  'IBOVESPA',
].join('|')

/**
 * Normaliza o nome cru de um indexador para o valor canônico da lista.
 * Ex: 'IPCA' → 'IPC-A', 'IBOVESPA' → 'IBOV', 'DOLAR' → 'DÓLAR'
 */
export function normalizeClienteBenchmarkIndexador(raw) {
  const map = {
    'IPCA': 'IPC-A', 'IBOVESPA': 'IBOV', 'DOLAR': 'DÓLAR',
    'S&P500': 'S&P 500', 'SP500': 'S&P 500',
    'IMAB': 'IMA-B', 'IRFM': 'IRF-M',
  }
  const upper = (raw || '').toUpperCase().trim()
  return map[upper] || upper
}

/**
 * Parseia uma fórmula de benchmark personalizado em estrutura {tipo, indexador, valor}.
 *
 * Aceita:
 *   - "120% CDI" → { tipo: 'pos', indexador: 'CDI', valor: 120 }
 *   - "IPC-A + 4,5%" → { tipo: 'pre', indexador: 'IPC-A', valor: 4.5 }
 *   - null/"" → { tipo: 'none', indexador: 'CDI', valor: null }
 */
export function parseClienteBenchmarkFormula(formula) {
  if (!formula) return { tipo: 'none', indexador: 'CDI', valor: null }
  const f = String(formula).trim()
  const pos = f.match(new RegExp(`^(\\d+(?:[.,]\\d+)?)%\\s*(${_INDEXADORES_REGEX_STR})$`, 'i'))
  if (pos) {
    return {
      tipo: 'pos',
      indexador: normalizeClienteBenchmarkIndexador(pos[2]),
      valor: parseFloat(pos[1].replace(',', '.')),
    }
  }
  const pre = f.match(new RegExp(`^(${_INDEXADORES_REGEX_STR})\\s*\\+\\s*(\\d+(?:[.,]\\d+)?)%$`, 'i'))
  if (pre) {
    return {
      tipo: 'pre',
      indexador: normalizeClienteBenchmarkIndexador(pre[1]),
      valor: parseFloat(pre[2].replace(',', '.')),
    }
  }
  return { tipo: 'none', indexador: 'CDI', valor: null }
}

/**
 * Inverso de `parseClienteBenchmarkFormula`: monta a fórmula a partir da
 * estrutura do formulário.
 *
 * Usado pelo cadastro do CLIENTE e pelo do GRUPO FAMILIAR — os dois gravam a
 * mesma fórmula, na mesma coluna, e o backend valida os dois com o mesmo
 * validador. Duas cópias desta montagem divergiriam no primeiro ajuste de
 * formato (vírgula, espaço, ordem), e o parser é literalmente um regex.
 *
 * @param {Object} meta - { tipo: 'none'|'pos'|'pre', indexador, valor }
 * @returns {String|null} fórmula ou `null` quando não há benchmark a gravar.
 */
export function buildClienteBenchmarkFormula(meta) {
  const m = meta || {}
  if (m.tipo === 'none') return null
  if (!m.indexador || !m.valor || m.valor <= 0) return null
  const valor = String(m.valor).replace('.', ',')
  if (m.tipo === 'pos') return `${valor}% ${m.indexador}`
  return `${m.indexador} + ${valor}%`
}

/**
 * @param {Object|null} clienteBenchmarkMeta - { label, logic } do cliente
 * @returns {string} '% CDI' (sem meta) | '(label) +' (logic plus) | '% (label)' (logic times)
 */
export function formatBenchmarkRelativeLabel(clienteBenchmarkMeta) {
  if (!clienteBenchmarkMeta) return '% CDI'
  const label = clienteBenchmarkMeta.label || 'CDI'
  const logic = clienteBenchmarkMeta.logic || 'times'
  return logic === 'plus' ? `(${label}) +` : `% (${label})`
}

const _DATASET_BASE = {
  fill: false,
  stepped: true,
  pointRadius: 0,
  borderWidth: 3,
  line: { borderDash: [0, 10], borderCapStyle: 'round' },
}

/**
 * Constrói o dataset do benchmark personalizado do cliente (puro, sem side-effects).
 *
 * @param {Object} cbMeta - { label, logic } do cliente
 * @param {Array}  cbSeries - pontos da série cumulativa
 * @returns {Object|null} dataset compatível com Chart.js, ou null se suprimido
 *                        por ser duplicata do CDI
 */
export function buildClienteBenchmarkDataset(cbMeta, cbSeries, { benchs = [] } = {}) {
  if (!cbMeta || !cbSeries || !cbSeries.length) return null
  const label = cbMeta.label || 'Meu Benchmark'
  const isCdiDuplicate =
    CDI_DEFAULT_META_LABELS.includes(label) &&
    benchs.some((b) => (b.nome || b) === 'CDI')
  if (isCdiDuplicate) return null
  return {
    ..._DATASET_BASE,
    label,
    data: cbSeries,
    borderColor: BENCHMARK_PERSONALIZADO_COLOR,
    backgroundColor: BENCHMARK_PERSONALIZADO_COLOR,
  }
}

/**
 * Injeta a linha do benchmark personalizado em mapLinesBenchmarks e em selecionados.
 *
 * - Usa vm.$set para garantir reatividade Vue 2 na propriedade nova.
 * - Adiciona CLIENT_BENCHMARK_KEY ao início de selecionados (somente se ausente).
 * - Remove o id do CDI de selecionados para evitar duplicata visual.
 *
 * @param {Object} options
 * @param {Vue}    options.vm                - instância Vue (para $set)
 * @param {Object} options.mapLinesBenchmarks - objeto reativo do mapa de datasets
 * @param {Array}  options.selecionados       - array de ids/keys selecionados (mutado in-place)
 * @param {Object} options.graphBenchmark     - r.value.graph.benchmark || {}
 * @param {Object} options.cbMeta             - clienteBenchmarkMeta
 * @param {Array}  options.benchs             - office benchmarks do Vuex store
 * @param {Object} [options.cdi]              - benchmark CDI do store (para remoção)
 */
export function injectClienteBenchmarkInGraph({
  vm,
  mapLinesBenchmarks,
  selecionados,
  graphBenchmark,
  cbMeta,
  benchs = [],
  cdi = null,
}) {
  const cbSeries = graphBenchmark[CLIENT_BENCHMARK_KEY]
  const dataset = buildClienteBenchmarkDataset(cbMeta, cbSeries, { benchs })
  if (!dataset) return

  vm.$set(mapLinesBenchmarks, CLIENT_BENCHMARK_KEY, dataset)

  if (!selecionados.includes(CLIENT_BENCHMARK_KEY)) {
    selecionados.unshift(CLIENT_BENCHMARK_KEY)
  }

  if (cdi && selecionados.includes(cdi.id)) {
    const filtered = selecionados.filter((id) => id !== cdi.id)
    selecionados.splice(0, selecionados.length, ...filtered)
  }
}
